import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import * as React from "react";
import { SimpleInput } from "@/core/components/ui/simple-input";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

export const SEARCH_INPUT_VARIANT = {
  /** The standard bordered field, for page headers and toolbars. */
  field: "field",
  /**
   * No box of its own, for a list's or pane's own header bar.
   *
   * A bordered field dropped into a narrow column reads as a form control
   * floating above the rows; the bar it sits in — a hairline under it — is what
   * frames it there.
   */
  bare: "bare",
} as const;

export type SearchInputVariant =
  (typeof SEARCH_INPUT_VARIANT)[keyof typeof SEARCH_INPUT_VARIANT];

export interface SearchInputProps
  extends Omit<React.ComponentProps<typeof SimpleInput>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  className?: string;
  placeholder?: string;
  variant?: SearchInputVariant;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onClear,
      className,
      placeholder = "Search",
      variant = SEARCH_INPUT_VARIANT.field,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    const handleClear = () => {
      onChange("");
      onClear?.();
    };

    if (variant === SEARCH_INPUT_VARIANT.bare) {
      return (
        <Stack
          direction="row"
          align="center"
          gap="sm"
          fullWidth
          className={cn("h-9", className)}
        >
          <MagnifyingGlassIcon className="size-4 shrink-0 text-tertiary-text" />
          <input
            ref={ref}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full bg-transparent text-md text-primary-text placeholder:text-tertiary-text focus:outline-none"
            {...props}
          />
          {value && (
            // A real button, not an icon with a click handler: clearing a search
            // is something a keyboard should reach.
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="shrink-0 rounded text-tertiary-text transition-colors hover:text-primary-text"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </Stack>
      );
    }

    return (
      <SimpleInput
        ref={ref}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        startAdornment={<MagnifyingGlassIcon className="h-6 w-6" />}
        endAdornment={
          value && (
            <XMarkIcon
              className="h-6 w-6 cursor-pointer"
              onClick={handleClear}
            />
          )
        }
        className={cn("m-px w-[300px]", className)}
        {...props}
      />
    );
  },
);

SearchInput.displayName = "SearchInput";
